import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { safeString } from "../../utils/strings";

export interface TextToSpeechEventData {
  name?: string;
  data?: {
    text?: string;
    voice?: string | { provider?: string; identifier?: string };
    cache?: boolean;
    interruptible?: boolean | null;
    instructions?: string;
    speed?: number;
    language?: string;
  };
  timestamp?: string;
  background?: boolean;
  background_task_name?: string | null;
}

// Helper to format voice display - handles both string and object formats
const formatVoice = (voice: string | { provider?: string; identifier?: string } | undefined): string | null => {
  if (!voice) return null;
  if (typeof voice === "string") return voice;
  // Handle object format with provider and identifier
  const parts: string[] = [];
  if (voice.identifier) parts.push(voice.identifier);
  if (voice.provider) parts.push(`(${voice.provider})`);
  return parts.length > 0 ? parts.join(" ") : null;
};

// Content component for the TTS tab
const TextToSpeechContent = ({ data }: { data: TextToSpeechEventData }) => {
  return (
    <View className="space-y-4">
      {/* Text */}
      {data.data?.text && (
        <View className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
          <Text className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{safeString(data.data.text)}</Text>
        </View>
      )}

      {/* Voice Settings */}
      <View className="flex flex-row flex-wrap gap-4">
        {/* Voice */}
        {formatVoice(data.data?.voice) && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="mic"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Voice: <span className="font-medium text-gray-800 dark:text-gray-200">{formatVoice(data.data?.voice)}</span>
            </Text>
          </View>
        )}

        {/* Language */}
        {data.data?.language && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="language"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Language: <span className="font-medium text-gray-800 dark:text-gray-200">{safeString(data.data.language)}</span>
            </Text>
          </View>
        )}

        {/* Speed */}
        {data.data?.speed !== undefined && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="activity"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              Speed: <span className="font-medium text-gray-800 dark:text-gray-200">{data.data.speed}x</span>
            </Text>
          </View>
        )}

        {/* Cache */}
        {data.data?.cache !== undefined && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name={data.data.cache ? "check" : "close"}
              size={14}
              className={data.data.cache ? "text-green-500" : "text-gray-400"}
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Cached</Text>
          </View>
        )}

        {/* Interruptible */}
        {data.data?.interruptible !== undefined && data.data?.interruptible !== null && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name={data.data.interruptible ? "check" : "close"}
              size={14}
              className={data.data.interruptible ? "text-green-500" : "text-gray-400"}
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Interruptible</Text>
          </View>
        )}

        {/* Background */}
        {data.background && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="layers"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">Background{data.background_task_name ? `: ${data.background_task_name}` : ""}</Text>
          </View>
        )}
      </View>

      {/* Instructions */}
      {data.data?.instructions && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="snippet"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Instructions</Text>
          </View>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
            <Text className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{safeString(data.data.instructions)}</Text>
          </View>
        </View>
      )}
    </View>
  );
};

// Main TextToSpeechSpanDetailView component with tabs
interface TextToSpeechSpanDetailViewProps {
  data: TextToSpeechEventData;
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "tts", label: "Text-to-Speech", color: "blue" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const TextToSpeechSpanDetailView = ({ data, rawData, className }: TextToSpeechSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("tts");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">{activeTab === "tts" ? <TextToSpeechContent data={data} /> : <RawSpanDetailView data={rawData} />}</View>
    </View>
  );
};

// Helper to parse text-to-speech event data from span attributes
export const parseTextToSpeechEventData = (attributes: Record<string, unknown>): TextToSpeechEventData | null => {
  const outputValue = attributes["output.value"];
  if (typeof outputValue !== "string") return null;

  try {
    const parsed = JSON.parse(outputValue);
    return parsed as TextToSpeechEventData;
  } catch {
    return null;
  }
};
